import { api } from '@creditsights/api-ui-web';
import { FundamentalsMetrics, RiskOverviewMetrics, RiskProductsData } from '../../models/RiskProducts';
import { ChangeChartDto, ChangeChartsDto } from '../dtos/riskProducts/changeCharts.dto';
import { RecentChangeListDto } from '../dtos/riskProducts/dashboard/recentChangeList.dto';
import {
  AcrTierMetric,
  CreditRiskLevelDto,
  CreditRiskLevelMetric,
  CreditRiskLevelsDto,
  HighLowCreditRiskLevelMetric,
  PositiveNegativeCreditRiskLevelMetric,
} from '../dtos/riskProducts/creditRiskLevels.dto';
import { DrillDownDto, DrillDownsDto } from '../dtos/riskProducts/drillDown.dto';
import { MonitorDto } from '../dtos/riskProducts/monitor.dto';
import {
  RiskDistributionDto,
  RiskDistributionGroupBy,
  RiskDistributionMetric,
  RiskDistributionsDto,
} from '../dtos/riskProducts/riskDistribution.dto';
import { RpColumnDto } from '../dtos/riskProducts/rpColumn.dto';
import { WidgetMetricResponseDto } from '../dtos/riskProducts/widgetMetric.dto';

export const riskProductsUri = '/risk-products/v1';
export const dashboardUri = `${riskProductsUri}/dashboard`;
export const getDashboardByWatchListUri = (watchListId: string): string => `${dashboardUri}/${watchListId}`;

export const getRiskProductUrl = (companyId: string): string => `${riskProductsUri}/company/${companyId}/riskoverview`;
export const getFundamentalsUrl = (companyId: string): string => `${riskProductsUri}/company/${companyId}/fundamentals`;

export const fetchCompanyRiskOverview = async (companyId: string): Promise<RiskOverviewMetrics> => {
  const resp = await api.get(getRiskProductUrl(companyId));
  return resp?.data?.data;
};

export const fetchCompanyChart = async (companyId: string, timeframe?: string): Promise<RiskProductsData> => {
  const resp = await api.get(`${riskProductsUri}/company/${companyId}/chart${timeframe ? `/${timeframe}` : ''}`);
  return resp.data;
};

export const fetchCompanyChartAllUsers = async (companyId: string): Promise<RiskProductsData> => {
  const resp = await api.get(`common/v1/issuers/${companyId}/riskchart/all-users/3y`);
  return resp.data;
};

export const fetchCompanyFundamentals = async (companyId: string): Promise<FundamentalsMetrics> => {
  const resp = await api.get(getFundamentalsUrl(companyId));
  return resp?.data;
};

const isMetricKeyValid = (creditRiskLevelsDto: CreditRiskLevelDto, metric: CreditRiskLevelMetric) => {
  switch (metric) {
    case CreditRiskLevelMetric.CQS:
      return Object.values(PositiveNegativeCreditRiskLevelMetric).includes(
        creditRiskLevelsDto.key as PositiveNegativeCreditRiskLevelMetric
      );
    case CreditRiskLevelMetric.CRL:
    case CreditRiskLevelMetric.FAS:
      return Object.values(HighLowCreditRiskLevelMetric).includes(
        creditRiskLevelsDto.key as HighLowCreditRiskLevelMetric
      );
    case CreditRiskLevelMetric.ACR:
      return true;
    default:
      return true;
  }
};

const filterUnrecognizedMetricKeys = (
  data: CreditRiskLevelsDto,
  metric: CreditRiskLevelMetric
): CreditRiskLevelDto[] => {
  return data.data.result
    .filter((creditRiskLevelsDto) => isMetricKeyValid(creditRiskLevelsDto, metric))
    .map((creditRiskLevelDto) => ({
      companyIds: creditRiskLevelDto.companyIds,
      key: creditRiskLevelDto.key,
    }));
};

const highLowCreditRiskLevelSortOrder = [
  HighLowCreditRiskLevelMetric.VERY_HIGH,
  HighLowCreditRiskLevelMetric.HIGH,
  HighLowCreditRiskLevelMetric.MODERATE,
  HighLowCreditRiskLevelMetric.LOW,
  HighLowCreditRiskLevelMetric.VERY_LOW,
  HighLowCreditRiskLevelMetric.NOT_AVAILABLE,
];

const hightLowCreditRiskLevelComparator = (a: CreditRiskLevelDto, b: CreditRiskLevelDto): number => {
  const indexA = highLowCreditRiskLevelSortOrder.indexOf(a.key as HighLowCreditRiskLevelMetric);
  const indexB = highLowCreditRiskLevelSortOrder.indexOf(b.key as HighLowCreditRiskLevelMetric);
  return indexA - indexB;
};

const positiveNegativeCreditRiskLevelSortOrder = [
  PositiveNegativeCreditRiskLevelMetric.POSITIVE,
  PositiveNegativeCreditRiskLevelMetric.STABLE,
  PositiveNegativeCreditRiskLevelMetric.NEGATIVE,
  PositiveNegativeCreditRiskLevelMetric.NOT_AVAILABLE,
];

const positiveNegativeCreditRiskLevelComparator = (a: CreditRiskLevelDto, b: CreditRiskLevelDto): number => {
  const indexA = positiveNegativeCreditRiskLevelSortOrder.indexOf(a.key as PositiveNegativeCreditRiskLevelMetric);
  const indexB = positiveNegativeCreditRiskLevelSortOrder.indexOf(b.key as PositiveNegativeCreditRiskLevelMetric);
  return indexA - indexB;
};

const acrTierSortOrder = [
  AcrTierMetric.AAA,
  AcrTierMetric.A,
  AcrTierMetric.BBB,
  AcrTierMetric.BB,
  AcrTierMetric.B,
  AcrTierMetric.CCC,
  AcrTierMetric.D,
  AcrTierMetric.NOT_AVAILABLE,
];

const acrTierComparator = (a: CreditRiskLevelDto, b: CreditRiskLevelDto): number => {
  const indexA = acrTierSortOrder.indexOf(a.key as AcrTierMetric);
  const indexB = acrTierSortOrder.indexOf(b.key as AcrTierMetric);

  if (indexA === -1) {
    return 1;
  }

  if (indexB === -1) {
    return -1;
  }

  return indexA - indexB;
};

const getCreditRiskLevelComparator = (metric: CreditRiskLevelMetric) => {
  switch (metric) {
    case CreditRiskLevelMetric.CQS:
      return positiveNegativeCreditRiskLevelComparator;
    case CreditRiskLevelMetric.CRL:
    case CreditRiskLevelMetric.FAS:
      return hightLowCreditRiskLevelComparator;
    case CreditRiskLevelMetric.ACR:
      return acrTierComparator;
    default:
      return (a, b) => a.key.localeCompare(b.key);
  }
};

export const fetchCreditRiskLevel = async (
  watchListId: string,
  metric: CreditRiskLevelMetric,
  rated = true
): Promise<CreditRiskLevelDto[]> => {
  const { data } = await api.get<CreditRiskLevelsDto>(
    `${getDashboardByWatchListUri(watchListId)}/credit-risk-level/${metric}/${rated}`
  );

  return filterUnrecognizedMetricKeys(data, metric).sort(getCreditRiskLevelComparator(metric));
};

export const fetchRiskDistribution = async (
  watchListId: string,
  groupBy: RiskDistributionGroupBy,
  metric?: RiskDistributionMetric,
  rated = true
): Promise<RiskDistributionDto[]> => {
  const { data } = await api.get<RiskDistributionsDto>(
    `${getDashboardByWatchListUri(watchListId)}/${groupBy}/portfolio-distribution/${rated}/${metric ?? ''}`
  );
  return data.data;
};

export const fetchChangeCharts = async (
  watchListId: string,
  groupBy: RiskDistributionGroupBy,
  rated = true
): Promise<ChangeChartDto[]> => {
  const { data } = await api.get<ChangeChartsDto>(
    `${getDashboardByWatchListUri(watchListId)}/change-charts/${groupBy}/${rated}`
  );
  return data.data;
};

export const fetchRecentChange = async (watchListId: string): Promise<RecentChangeListDto> => {
  const { data } = await api.get<RecentChangeListDto>(`${getDashboardByWatchListUri(watchListId)}/recent-changes`);
  return data;
};

export const fetchDrillDown = async (companies: number[]): Promise<DrillDownDto[]> => {
  const { data } = await api.post<DrillDownsDto>(`${dashboardUri}/drilldown`, { companyIds: companies });
  return data.data;
};

export const fetchRpColumns = async (): Promise<RpColumnDto[]> => {
  const { data } = await api.get<RpColumnDto[]>(`${riskProductsUri}/rp-column`);
  return data;
};

export const fetchMonitor = async (watchListId: string): Promise<MonitorDto[]> => {
  const { data } = await api.get<{ monitor: MonitorDto[] }>(`${getDashboardByWatchListUri(watchListId)}/monitor`);
  return data.monitor;
};

export const fetchWidgetMetrics = async (): Promise<WidgetMetricResponseDto[]> => {
  const { data } = await api.get<WidgetMetricResponseDto[]>(`${dashboardUri}/widget-metric`);
  return data;
};
